import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Client } from '@twilio/conversations';
import { ChatContext, type ChatContextType } from './ChatContext';
import { useChatToken } from '../../Lib/Hooks/chat.hooks';
import {
  addConversation,
  addMessage,
  invalidateLastMessage,
  removeConversation,
  removeMessage,
  removeUser,
  updateConversation,
  updateMessage,
  updateUser,
} from './cache';
import { ChatUserType } from './types';

export type ChatProviderProps = {
  userType: ChatUserType;
  userId: string;
  children: React.ReactNode;
};

export function ChatProvider(props: ChatProviderProps) {
  const { userType, userId, children } = props;

  const tokenProcess = useChatToken(userId);
  const token = tokenProcess.data?.token;
  const tokenError = tokenProcess.isError;
  const refetchToken = tokenProcess.refetch;

  const queryClient = useQueryClient();

  const [state, setState] = useState<
    Omit<ChatContextType, 'userType' | 'userId' | 'identity'>
  >({
    status: 'unknown',
    client: undefined,
    error: undefined,
  });

  useEffect(() => {
    if (!token) {
      setState({
        status: 'unknown',
        client: undefined,
        error: undefined,
      });
      return () => {};
    }

    const client = new Client(token);
    setState({
      status: 'unknown',
      client,
      error: undefined,
    });

    client.on('connectionStateChanged', (status) => {
      setState((prev) => ({ ...prev, status }));
    });

    client.on('connectionError', (error) => {
      console.error('Twilio: connectionError', error);
      setState((prev) => ({ ...prev, error: error.message }));
    });

    client.on('initialized', () => {});

    client.on('initFailed', (error) => {
      console.error('Twilio: initFailed', error);
      setState((prev) => ({
        ...prev,
        error: error?.error?.message || 'Chat initialization failed.',
      }));
    });

    client.on('conversationAdded', (conversation) => {
      addConversation(queryClient, userId, conversation);
    });

    client.on('conversationJoined', (conversation) => {
      addConversation(queryClient, userId, conversation);
    });

    client.on('conversationRemoved', (conversation) => {
      removeConversation(queryClient, userId, conversation);
    });

    client.on('conversationLeft', (conversation) => {
      removeConversation(queryClient, userId, conversation);
    });

    client.on('conversationUpdated', ({ conversation, updateReasons }) => {
      updateConversation(queryClient, userId, conversation);
      if (updateReasons.includes('lastMessage')) {
        invalidateLastMessage(queryClient, userId, conversation);
      }
    });

    client.on('messageAdded', (message) => {
      addMessage(queryClient, userId, message);
    });

    client.on('messageUpdated', ({ message }) => {
      updateMessage(queryClient, userId, message);
    });

    client.on('messageRemoved', (message) => {
      removeMessage(queryClient, userId, message);
    });

    client.on('userSubscribed', (user) => {
      updateUser(queryClient, userId, user);
    });

    client.on('userUnsubscribed', (user) => {
      removeUser(queryClient, userId, user);
    });

    client.on('userUpdated', ({ user }) => {
      updateUser(queryClient, userId, user);
    });

    client.on('tokenAboutToExpire', () => {
      refetchToken().catch(() => {});
    });

    client.on('tokenExpired', () => {
      refetchToken().catch(() => {});
    });

    return () => {
      client.removeAllListeners();
      client.shutdown().catch((e) => {
        console.error('Twilio: shutdown failed', e);
      });
    };
  }, [token]);

  const context = useMemo<ChatContextType>(
    () => ({
      userType,
      userId,
      identity: `${userType}_${userId}`,
      status: state.status,
      client: state.client,
      error: tokenError ? 'Failed to get chat token.' : state.error,
    }),
    [userType, userId, state.status, state.client, state.error, tokenError],
  );

  return (
    <ChatContext.Provider value={context}>{children}</ChatContext.Provider>
  );
}
